import React, {useState} from "react";
import {Query} from "react-apollo";
import {Link} from "react-router";
import {fromJS, List} from "immutable";
import Table from "../../../components/Table";
import Loading from "../../../components/Loading";
import GraphQLErrors from "../../../components/GraphQLErrors";
import ActionButton from "../../../components/ActionButton";
import {CLIENT_SHIPPED_SERIALS, CLIENT_DISPATCHES_MISSING_SERIALS} from "../../../queries/clientDispatches";
import {LIST_PARTNERS} from "../../../queries/partners";
import "./ShippedSerialsReport.scss";

// Which containers went to which client, one row per serial number: the record
// regulators ask for. Filter by client and by the days they shipped, then
// download it as CSV.

const tableHeader = List(["Serial", "SKU", "Client", "Client Reference", "Dispatch #", "From", "Shipped"]);

const COLUMNS = [
  ["serial", "Serial"],
  ["sku", "SKU"],
  ["partnerName", "Client"],
  ["reference", "Client Reference"],
  ["clientDispatchId", "Dispatch"],
  ["originWarehouseId", "From"],
  ["shippedAt", "Shipped (UTC)"],
];

function csvCell(value) {
  const text = value == null ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadCsv(rows, filename) {
  const lines = [COLUMNS.map(([, title]) => title).join(",")]
    .concat(rows.map((row) => COLUMNS.map(([key]) => csvCell(row[key])).join(",")));
  const blob = new Blob([lines.join("\n") + "\n"], {type: "text/csv;charset=utf-8"});
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const SerialRow = (props) => {
  const {data} = props;
  return (
    <tr className="item">
      <td className="field f-id">{data.get("serial")}</td>
      <td className="field">{data.get("sku")}</td>
      <td className="field">{data.get("partnerName")}</td>
      <td className="field">{data.get("reference")}</td>
      <td className="field f-id">{data.get("clientDispatchId")}</td>
      <td className="field f-warehouse">{data.get("originWarehouseId")}</td>
      <td className="field f-date">{String(data.get("shippedAt") || "").substr(0, 16).replace("T", " ")}</td>
    </tr>
  )
}

// Dispatches in the same period that shipped without all their serial numbers:
// the units this report cannot name yet.
const MissingSerials = ({variables}) => (
  <Query query={CLIENT_DISPATCHES_MISSING_SERIALS} variables={variables} fetchPolicy="network-only">
    {({data}) => {
      const dispatches = (data && data.clientDispatchesMissingSerials) || [];
      if (dispatches.length === 0) return null;
      const units = dispatches.reduce((sum, dispatch) => sum + dispatch.serialsPending, 0);
      return (
        <div className="missing">
          <p>
            {units} units shipped in this period are not in this report yet: their serial numbers are
            missing from {dispatches.length === 1 ? "this dispatch" : `these ${dispatches.length} dispatches`}.
          </p>
          <ul>
            {dispatches.map((dispatch) => (
              <li key={dispatch.id}>
                <Link to={`/client-dispatches/${dispatch.id}`}>{dispatch.partnerName} {dispatch.reference || dispatch.id}</Link>
                {dispatch.stordOrderNumber ? ` (Stord ${dispatch.stordOrderNumber})` : ""}: {dispatch.serialsPending} missing
              </li>
            ))}
          </ul>
        </div>
      )
    }}
  </Query>
);

const ShippedSerialsReport = () => {
  const [partnerId, setPartnerId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const variables = {partnerId: partnerId || null, from: from || null, to: to || null};

  return (
    <div className="ShippedSerialsReport">
      <div className="filters">
        <Query query={LIST_PARTNERS} variables={{role: "client"}}>
          {({data}) => (
            <label>
              Client
              <select id="report-client" value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
                <option value="">All clients</option>
                {((data && data.partners) || []).map((partner) =>
                  <option key={partner.partnerId} value={partner.partnerId}>{partner.name}</option>
                )}
              </select>
            </label>
          )}
        </Query>
        <label>
          Shipped from
          <input id="report-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          to
          <input id="report-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>
      <MissingSerials variables={variables} />
      <Query query={CLIENT_SHIPPED_SERIALS} variables={variables} fetchPolicy="network-only">
        {({loading, error, data}) => {
          if (loading) return <Loading />
          if (error) return <GraphQLErrors error={error} />
          const rows = data.clientShippedSerials || [];
          return (
            <React.Fragment>
              <div className="summary">
                <span>{rows.length} serial numbers</span>
                <ActionButton label="Download CSV" active={rows.length > 0}
                  onClick={() => downloadCsv(rows, `shipped-serials${from ? "-" + from : ""}${to ? "-to-" + to : ""}.csv`)} />
              </div>
              <Table
                data={fromJS(rows)}
                renderer={SerialRow}
                onItemClick={() => null}
                tableHeader={tableHeader}
                extraClassName="ShippedSerialsList"
                itemKey="serial"
                mode="table"
                />
            </React.Fragment>
          )
        }}
      </Query>
    </div>
  )
}

export default ShippedSerialsReport;
